import { contract_address, getChainInfoById } from "@/constants/const";
import { StakingViewer_Contract_ABI } from "@/constants/abi";
import { JsonRpcProvider, Contract, parseUnits, formatUnits } from "ethers";
import { formatDecimalWithSeparation } from "celer-web-utils/lib/format";

export const getWidth = (): number => {
  if (typeof window !== "undefined") {
    return window.innerWidth;
  }
  return 0;
};

export const formatTokenToUSD = (
  rawAmount: string | undefined,
  decimals: number | undefined,
  price: number
): number => {
  if (rawAmount === undefined || decimals === 0 || price === 0) return 0;
  const number = Number(
    formatDecimalWithSeparation(
      rawAmount || "0",
      6,
      decimals,
      "floor",
      "",
      true
    )
  );
  const usd = number * price;
  return usd;
};

export function formatTokenAmount(
  rawValue: BigInt,
  decimals: number,
  symbol: string
): string {
  return `${(Number(rawValue) / 10 ** Number(decimals)).toFixed(2)} ${symbol}`;
}

export interface GetAPRParams {
  provider: JsonRpcProvider;
  contract: Contract;
  shareDecimals?: number;
  shareAmount?: string;
  daysAgo?: number;
  useBatch?: boolean;
}

export async function getAPR(provers: string[]): Promise<number[]> {
  if (!provers || provers.length === 0) return [];

  const USDC_DECIMALS = Number(localStorage.getItem("decimals")) || 6;
  const ONE_SHARE = parseUnits("1", USDC_DECIMALS);
  const shares = Array(provers.length).fill(ONE_SHARE);

  const provider = new JsonRpcProvider(
    getChainInfoById(
      Number(process.env.REACT_APP_PUBLIC_CLAIM_CHAIN || 59144)
    ).rpcUrl
  );

  const staking_viewer_contract = new Contract(
    contract_address.staking_viewer,
    StakingViewer_Contract_ABI,
    provider
  );

  const SECONDS_PER_YEAR = 365 * 24 * 3600;

  async function findBlockByTimestamp(
    targetTimestamp: number
  ): Promise<number> {
    let low = 0;
    let high = await provider.getBlockNumber();

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const block = await provider.getBlock(mid);
      if (!block) throw new Error(`Block ${mid} not found`);

      if (block.timestamp > targetTimestamp) high = mid - 1;
      else low = mid + 1;
    }
    return high;
  }

  // 1. now
  const latestBlock = await provider.getBlock("latest");
  if (!latestBlock) throw new Error("Cannot fetch latest block");
  const t_now = latestBlock.timestamp;

  const P_nows_raw = await staking_viewer_contract.batchConvertToAssets(
    provers,
    shares
  );
  const P_nows = Array.from(P_nows_raw);

  // 2. old block
  const targetTimestamp = t_now - 7 * 24 * 3600;
  const oldBlockNumber = await findBlockByTimestamp(targetTimestamp);
  const oldBlock = await provider.getBlock(oldBlockNumber);
  if (!oldBlock) throw new Error(`Block ${oldBlockNumber} not found`);
  const t_old = oldBlock.timestamp;

  // 3. old values
  const P_olds_raw = await staking_viewer_contract.batchConvertToAssets(
    provers,
    shares,
    { blockTag: oldBlockNumber }
  );
  const P_olds = Array.from(P_olds_raw);

  // 4. APR calculation
  const dt = t_now - t_old;

  const res = P_nows.map((P_now: any, i: number) => {
    const P_old: any = P_olds[i];

    const P_now_f = parseFloat(formatUnits(P_now, USDC_DECIMALS));
    const P_old_f = parseFloat(formatUnits(P_old, USDC_DECIMALS));

    const R = P_now_f / P_old_f - 1;
    const APR = R * (SECONDS_PER_YEAR / dt);

    return APR;
  });

  return res;
}
